import React from "react";
import Header from "./Header";

const TermsOfUse = () => {
  return (
    <>
      <Header />
      <div className="min-h-screen bg-white">
        <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-8">Terms of Use</h1>
          
          <div className="prose prose-emerald max-w-none">
            <p className="text-lg text-gray-500 mb-8">
              Last updated: February 11, 2025
            </p>

            {/* 1. Acceptance of Terms */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-600 mb-4">
                Welcome to Kolanut! These Terms of Use govern your use of our website and services. By accessing or using Kolanut, you agree to be bound by these terms. By using our site, you agree to comply with and be legally bound by these Terms of Use and our Privacy Policy.
              </p>
            </section>

            {/* 2. Use of Service */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Use of Service</h2>
              <p className="text-gray-600 mb-4">
                You agree to use Kolanut only for lawful purposes and in accordance with these terms. You are responsible for your use of the site and any content you provide.
              </p>
            </section>

            {/* 3. Intellectual Property */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Intellectual Property</h2>
              <p className="text-gray-600 mb-4">
                All content, trademarks, and data on this site, including but not limited to software, databases, text, graphics, icons, and hyperlinks are the property of Kolanut or its licensors.
              </p>
            </section>

            {/* 4. Changes to Terms */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Changes to Terms</h2>
              <p className="text-gray-600 mb-4">
                We reserve the right to update or change these Terms of Use at any time. Your continued use of the site after any changes constitutes acceptance of those changes.
              </p>
            </section>

            {/* 5. SMS Marketing Terms */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. SMS Marketing Terms</h2>
              
              <div className="text-gray-600 space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">5.1 SMS Marketing Program</h3>
                  <p className="mb-3">
                    Kolanut offers SMS Marketing services that allow you to send marketing text messages to subscribers who have opted in to receive SMS communications from you. SMS Marketing is available as an add-on service and requires separate registration and approval.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">5.2 Registration and Approval</h3>
                  <p className="mb-3">
                    To use SMS Marketing services, you must submit an application and receive approval. The application process requires:
                  </p>
                  <ul className="list-disc list-inside ml-4 space-y-2 mb-3">
                    <li>Business information and required documentation</li>
                    <li>Selection of sending country and phone number</li>
                    <li>Verification of registered sending numbers</li>
                    <li>Privacy Policy specific to SMS marketing</li>
                    <li>Terms and Conditions specific to SMS marketing</li>
                    <li>Monthly SMS credit subscription setup</li>
                  </ul>
                  <p className="mb-3">
                    Application processing typically takes 1-2 business days. You will receive email notification once your application has been processed and approved.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">5.3 Subscriber Consent and Opt-In</h3>
                  <p className="mb-3">
                    You agree to only send SMS messages to contacts who have explicitly opted in to receive SMS marketing from you. You are responsible for:
                  </p>
                  <ul className="list-disc list-inside ml-4 space-y-2 mb-3">
                    <li>Obtaining proper consent before sending SMS messages</li>
                    <li>Maintaining records of opt-in consent</li>
                    <li>Honoring opt-out requests immediately</li>
                    <li>Complying with double opt-in requirements when applicable (e.g., for abandoned cart messages)</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">5.4 SMS Credits and Billing</h3>
                  <p className="mb-3">
                    SMS Marketing requires a monthly credit subscription. Key terms:
                  </p>
                  <ul className="list-disc list-inside ml-4 space-y-2 mb-3">
                    <li>SMS credits are required to send text messages</li>
                    <li>Credit costs vary by sending country</li>
                    <li>For US programs, each SMS message typically costs 1-3 credits</li>
                    <li>Your first SMS credit subscription purchase will be prorated based on the number of days left in your billing cycle</li>
                    <li>The payment method on file for your account is automatically used for monthly SMS credit subscriptions</li>
                    <li>You can change your monthly SMS subscription or payment information at any time</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">5.5 Compliance and Restrictions</h3>
                  <p className="mb-3">
                    You agree to comply with all applicable laws and regulations regarding SMS marketing, including:
                  </p>
                  <ul className="list-disc list-inside ml-4 space-y-2 mb-3">
                    <li>TCPA (Telephone Consumer Protection Act) in the United States</li>
                    <li>Country-specific quiet hours and rate limits</li>
                    <li>Carrier requirements and restrictions</li>
                    <li>Prohibited use cases as outlined in our SMS Marketing guidelines</li>
                  </ul>
                  <p className="mb-3">
                    You are responsible for knowing and complying with quiet hours within your SMS program's country. You must not use SMS Marketing for illegal, fraudulent, or prohibited purposes.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">5.6 Privacy Policy and Terms Requirements</h3>
                  <p className="mb-3">
                    You must maintain an online Privacy Policy and Terms and Conditions that are specific to SMS marketing. These documents must:
                  </p>
                  <ul className="list-disc list-inside ml-4 space-y-2 mb-3">
                    <li>Clearly describe how you collect, use, and manage personal information for SMS marketing</li>
                    <li>Explain how subscribers can opt-out of SMS communications</li>
                    <li>Be accessible via a direct link (not a redirected URL)</li>
                    <li>Be updated to reflect your SMS marketing practices</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">5.7 Opt-Out and Unsubscribe</h3>
                  <p className="mb-3">
                    Subscribers must be able to opt-out of SMS communications at any time by replying STOP, UNSUBSCRIBE, or using other standard opt-out methods. You agree to:
                  </p>
                  <ul className="list-disc list-inside ml-4 space-y-2 mb-3">
                    <li>Process opt-out requests immediately</li>
                    <li>Not send further messages to subscribers who have opted out</li>
                    <li>Provide clear opt-out instructions in your SMS messages</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">5.8 Program Deactivation</h3>
                  <p className="mb-3">
                    You may deactivate your SMS Marketing program at any time through your account settings. Upon deactivation:
                  </p>
                  <ul className="list-disc list-inside ml-4 space-y-2 mb-3">
                    <li>You will no longer be able to send SMS messages</li>
                    <li>Your monthly SMS credit subscription will be cancelled</li>
                    <li>You may reactivate your program subject to re-approval if needed</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">5.9 Limitation of Liability</h3>
                  <p className="mb-3">
                    Kolanut is not responsible for the content of SMS messages you send or for any violations of applicable laws or regulations by you or your use of SMS Marketing services. You are solely responsible for ensuring compliance with all applicable laws, regulations, and carrier requirements.
                  </p>
                </div>
              </div>
            </section>

            {/* 6. Contact Us */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Contact Us</h2>
              <p className="text-gray-600 mb-4">
                If you have any questions about these Terms, please contact us at <strong>admin@rezonata.xyz</strong>.
              </p>
            </section>
          </div>

          <footer className="mt-12 pt-8 border-t border-gray-200">
            <p className="text-gray-500 text-sm text-center">
              © 2026 Rezonata Labs Inc. All rights reserved.
            </p>
          </footer>
        </div>
      </div>
    </>
  );
};

export default TermsOfUse; 